import { supabase } from '../lib/supabase';

// Helper function to get current user - supports both Supabase Auth and test users
const getCurrentUser = async (providedUser = null) => {
  // If user is provided directly, use it
  if (providedUser) {
    return providedUser;
  }

  // Otherwise, try to get from Supabase Auth
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// UPDATED: TIME SLOT DEFINITIONS - Now 6 slots instead of 5
export const TIME_SLOTS = {
  'early': { label: 'Early', start: '06:00', end: '12:00' },
  'morning': { label: 'Morning', start: '08:00', end: '14:00' },
  'midday': { label: 'Midday', start: '10:00', end: '16:00' },
  'afternoon': { label: 'Afternoon', start: '12:00', end: '18:00' },
  'evening': { label: 'Evening', start: '14:00', end: '20:00' },
  'late': { label: 'Late', start: '16:00', end: '22:00' }
};

// CONVERSION FUNCTIONS

// Convert selected slots to time ranges
export const slotsToTimeRanges = (selectedSlots) => {
  const ranges = Array.from(selectedSlots).map(slotKey => {
    const slot = TIME_SLOTS[slotKey];
    return { start: slot.start, end: slot.end };
  });
  // Sort by start time and merge overlapping ranges
  return mergeOverlappingRanges(ranges);
};

// Convert time ranges back to slots where possible
export const timeRangesToSlots = (timeRanges) => {
  const matchedSlots = new Set();
  timeRanges.forEach(range => {
    Object.entries(TIME_SLOTS).forEach(([slotKey, slotData]) => {
      if (rangesOverlapSignificantly(range, slotData)) {
        matchedSlots.add(slotKey);
      }
    });
  });
  return matchedSlots;
};


// Helper function: Check if two time ranges overlap significantly
const rangesOverlapSignificantly = (range1, range2) => {
  const [start1Hours, start1Minutes] = range1.start.split(':').map(Number);
  const [end1Hours, end1Minutes] = range1.end.split(':').map(Number);
  const [start2Hours, start2Minutes] = range2.start.split(':').map(Number);
  const [end2Hours, end2Minutes] = range2.end.split(':').map(Number);

  const start1Total = start1Hours * 60 + start1Minutes;
  const end1Total = end1Hours * 60 + end1Minutes;
  const start2Total = start2Hours * 60 + start2Minutes;
  const end2Total = end2Hours * 60 + end2Minutes;

  const overlapStart = Math.max(start1Total, start2Total);
  const overlapEnd = Math.min(end1Total, end2Total);
  const overlapMinutes = Math.max(0, overlapEnd - overlapStart);

  // Consider significant if overlap is at least 2 hours (120 minutes)
  return overlapMinutes >= 120;
};

// Helper function: Check if two sets of time ranges are equivalent
const timeRangesEquivalent = (ranges1, ranges2) => {
  if (ranges1.length !== ranges2.length) return false;

  // Sort both arrays by start time
  const sorted1 = [...ranges1].sort((a, b) => a.start.localeCompare(b.start));
  const sorted2 = [...ranges2].sort((a, b) => a.start.localeCompare(b.start));

  // Compare each range
  for (let i = 0; i < sorted1.length; i++) {
    if (sorted1[i].start !== sorted2[i].start || sorted1[i].end !== sorted2[i].end) {
      return false;
    }
  }

  return true;
};

// Save user availability data with preference type field (no conversion needed)
export const saveUserAvailability = async (availabilityData, userPreference = 'available', currentUser = null) => {
  try {

    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('❌ User not authenticated in saveUserAvailability');
      throw new Error('User not authenticated');
    }
    

    // Delete existing records for this preference type only
    const { error: deleteError } = await supabase
      .from('user_availability')
      .delete()
      .eq('user_id', user.id)
      .eq('preference_type', userPreference);

    if (deleteError) {
      console.error('❌ Error deleting existing availability:', deleteError);
      throw deleteError;
    }
    

    // Prepare new availability records - store exactly what user selected
    const availabilityRecords = [];
    Object.entries(availabilityData).forEach(([date, timeRanges]) => {
      
      if (timeRanges && timeRanges.length > 0) {
        timeRanges.forEach((range, index) => {
          // Validate that range has start and end times
          if (range.start && range.end && typeof range.start === 'string' && typeof range.end === 'string') {
            const record = {
              user_id: user.id,
              availability_date: date,
              start_time: range.start,
              end_time: range.end,
              preference_type: userPreference, // Store the preference type
              is_calendar_synced: false, // Mark as manual entry (not from calendar sync)
              // Keep time_slots for backward compatibility
              time_slots: range.start
            };
            availabilityRecords.push(record);
          } else {
            console.warn(`Invalid range data:`, range);
          }
        });
      }
    });


    // Insert new availability records if any exist
    if (availabilityRecords.length > 0) {
      // Insert in batches to avoid potential issues with large datasets
      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < availabilityRecords.length; i += batchSize) {
        batches.push(availabilityRecords.slice(i, i + batchSize));
      }
      

      for (const batch of batches) {
        const { data, error: insertError } = await supabase
          .from('user_availability')
          .insert(batch)
          .select();

        if (insertError) {
          console.error('❌ Error inserting availability batch:', insertError);
          throw insertError;
        }
        
      }

      return { data: availabilityRecords, error: null };
    }
    
    return { data: [], error: null };
  } catch (error) {
    console.error('❌ Exception in saveUserAvailability:', error);
    return { data: null, error };
  }
};

// Get user availability data with calendar-first approach
export const getUserAvailability = async (userPreference = 'available', currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('❌ User not authenticated in getUserAvailability');
      throw new Error('User not authenticated');
    }

    // Check if user has calendar connected (Outlook, Google, or ICS)
    const { data: calendarConnection } = await supabase
      .from('calendar_connections')
      .select('id, sync_enabled, provider')
      .eq('user_id', user.id)
      .eq('sync_enabled', true)
      .in('provider', ['outlook', 'google'])
      .maybeSingle();

    // Also check for ICS subscriptions
    const { data: icsSubscriptions } = await supabase
      .from('ics_subscriptions')
      .select('id, sync_enabled')
      .eq('user_id', user.id)
      .eq('sync_enabled', true);

    const hasOAuthCalendar = calendarConnection && calendarConnection.sync_enabled;
    const hasIcsCalendar = icsSubscriptions && icsSubscriptions.length > 0;
    const isCalendarConnected = hasOAuthCalendar || hasIcsCalendar;

    // Get the sync date range from the most recent successful sync log
    let syncDateRange = null;
    if (hasOAuthCalendar && calendarConnection) {
      const { data: syncLog } = await supabase
        .from('calendar_sync_logs')
        .select('sync_start_date, sync_end_date, sync_status')
        .eq('connection_id', calendarConnection.id)
        .eq('sync_status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (syncLog && syncLog.sync_start_date && syncLog.sync_end_date) {
        syncDateRange = {
          startDate: syncLog.sync_start_date,
          endDate: syncLog.sync_end_date
        };
      }
    }

    let busyTimeData = {};
    let availableTimeData = {};

    if (isCalendarConnected) {
      // Calendar is connected - use ONLY calendar data (ignore manual)
      const { data: calendarData, error: calendarError } = await supabase
        .from('user_availability')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_calendar_synced', true)
        .order('availability_date', { ascending: true });

      if (calendarError) {
        console.error('❌ Database error fetching calendar availability:', calendarError);
        throw calendarError;
      }

      // Process calendar data (stored as busy times)
      calendarData?.forEach((record) => {
        if (!busyTimeData[record.availability_date]) {
          busyTimeData[record.availability_date] = [];
        }

        if (record.start_time && record.end_time) {
          const startTime = record.start_time.substring(0, 5);
          const endTime = record.end_time.substring(0, 5);
          busyTimeData[record.availability_date].push({
            start: startTime,
            end: endTime
          });
        }
      });
    } else {
      // No calendar connected - use manual data
      // Query for records that are NOT calendar synced (false or null for backwards compatibility)
      const { data: manualData, error: manualError } = await supabase
        .from('user_availability')
        .select('*')
        .eq('user_id', user.id)
        .or('is_calendar_synced.eq.false,is_calendar_synced.is.null')
        .order('availability_date', { ascending: true });

      if (manualError) {
        console.error('❌ Database error fetching manual availability:', manualError);
        throw manualError;
      }

      // Process manual data - separate by preference type
      manualData?.forEach((record) => {
        const preferenceType = record.preference_type || 'available';

        if (record.start_time && record.end_time) {
          const startTime = record.start_time.substring(0, 5);
          const endTime = record.end_time.substring(0, 5);
          const timeRange = { start: startTime, end: endTime };

          // Store in the appropriate container based on original preference
          if (preferenceType === 'available') {
            if (!availableTimeData[record.availability_date]) {
              availableTimeData[record.availability_date] = [];
            }
            availableTimeData[record.availability_date].push(timeRange);
          } else {
            // 'unavailable' or any other value treated as busy
            if (!busyTimeData[record.availability_date]) {
              busyTimeData[record.availability_date] = [];
            }
            busyTimeData[record.availability_date].push(timeRange);
          }
        } else if (record.time_slots && record.time_slots.trim()) {
          // Backward compatibility for old format
          const timeSlot = record.time_slots.trim();
          const [startHour] = timeSlot.split('-')[0].split(':').map(Number);
          const endHour = startHour + 2;
          const timeRange = {
            start: `${String(startHour).padStart(2, '0')}:00`,
            end: `${String(Math.min(endHour, 22)).padStart(2, '0')}:00`
          };

          if (preferenceType === 'available') {
            if (!availableTimeData[record.availability_date]) {
              availableTimeData[record.availability_date] = [];
            }
            availableTimeData[record.availability_date].push(timeRange);
          } else {
            if (!busyTimeData[record.availability_date]) {
              busyTimeData[record.availability_date] = [];
            }
            busyTimeData[record.availability_date].push(timeRange);
          }
        }
      });
    }

    // Merge overlapping ranges for each date
    Object.keys(busyTimeData).forEach(date => {
      busyTimeData[date] = mergeOverlappingRanges(busyTimeData[date]);
    });
    Object.keys(availableTimeData).forEach(date => {
      availableTimeData[date] = mergeOverlappingRanges(availableTimeData[date]);
    });

    // Apply display preference (mode toggle)
    let displayData = {};

    console.log('getUserAvailability - userPreference:', userPreference);
    console.log('getUserAvailability - busyTimeData:', JSON.stringify(busyTimeData));
    console.log('getUserAvailability - availableTimeData:', JSON.stringify(availableTimeData));

    if (userPreference === 'available') {
      // User wants to see AVAILABLE times
      if (isCalendarConnected) {
        // For calendar sync: convert busy times to show available gaps
        console.log('Calendar connected - converting busy times to available times');
        Object.keys(busyTimeData).forEach(date => {
          displayData[date] = convertBusyTimesToAvailable(busyTimeData[date]);
        });
      } else {
        // For manual data: show times that were marked as available
        console.log('Showing manually marked available times');
        displayData = availableTimeData;
      }
    } else {
      // User wants to see BUSY times
      console.log('Showing busy times');
      displayData = busyTimeData;
    }

    console.log('getUserAvailability - displayData after conversion:', JSON.stringify(displayData));
    console.log('getUserAvailability - syncDateRange:', syncDateRange);

    return {
      data: displayData,
      error: null,
      isCalendarConnected,
      syncDateRange  // Include the sync date range so UI knows which dates were actually checked
    };
  } catch (error) {
    console.error('❌ Exception in getUserAvailability:', error);
    return { data: {}, error, isCalendarConnected: false, syncDateRange: null };
  }
};

// Helper function to merge overlapping time ranges
const mergeOverlappingRanges = (ranges) => {
  if (ranges.length <= 1) return ranges;

  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  const merged = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    // Check if ranges overlap or are adjacent
    if (current.start <= last.end) {
      last.end = current.end > last.end ? current.end : last.end;
    } else {
      merged.push(current);
    }
  }

  return merged;
};

// Helper function to find intersection of two sets of time ranges
// Returns only the times that exist in BOTH sets
const findTimeRangeIntersection = (ranges1, ranges2) => {
  if (!ranges1 || ranges1.length === 0 || !ranges2 || ranges2.length === 0) {
    return [];
  }

  const intersections = [];

  // For each range in set 1
  ranges1.forEach(range1 => {
    const start1 = timeToMinutes(range1.start);
    const end1 = timeToMinutes(range1.end);

    // Check against each range in set 2
    ranges2.forEach(range2 => {
      const start2 = timeToMinutes(range2.start);
      const end2 = timeToMinutes(range2.end);

      // Find overlap
      const overlapStart = Math.max(start1, start2);
      const overlapEnd = Math.min(end1, end2);

      // If there's an overlap, add it
      if (overlapStart < overlapEnd) {
        intersections.push({
          start: minutesToTime(overlapStart),
          end: minutesToTime(overlapEnd)
        });
      }
    });
  });

  // Merge any overlapping intersections
  return mergeOverlappingRanges(intersections);
};

// AVAILABILITY PREFERENCE CONVERSION FUNCTIONS

// Convert time to minutes since midnight
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Convert minutes since midnight back to time string
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/**
 * Convert busy times to available times by finding the gaps
 * Spans the full 24h day (00:00 - 24:00) so every timezone / season is covered;
 * daylight-aware scoring decides which of those hours are sensible to play.
 */
export const convertBusyTimesToAvailable = (busyTimeRanges) => {

  if (!busyTimeRanges || busyTimeRanges.length === 0) {
    // If no busy times, entire day is available
    return [{ start: '00:00', end: '24:00' }];
  }

  const golfDayStart = timeToMinutes('00:00'); // midnight
  const golfDayEnd = timeToMinutes('24:00');   // end of day

  // Convert busy ranges to minutes and sort
  const busyMinutes = busyTimeRanges
    .map(range => ({
      start: Math.max(timeToMinutes(range.start), golfDayStart),
      end: Math.min(timeToMinutes(range.end), golfDayEnd)
    }))
    .sort((a, b) => a.start - b.start);

  // Merge overlapping busy periods
  const mergedBusy = [];
  for (const range of busyMinutes) {
    if (mergedBusy.length === 0 || range.start > mergedBusy[mergedBusy.length - 1].end) {
      mergedBusy.push(range);
    } else {
      mergedBusy[mergedBusy.length - 1].end = Math.max(mergedBusy[mergedBusy.length - 1].end, range.end);
    }
  }

  // Find available periods (gaps between busy periods)
  const availableRanges = [];

  // Check if there's available time before the first busy period
  if (mergedBusy.length === 0 || mergedBusy[0].start > golfDayStart) {
    availableRanges.push({
      start: minutesToTime(golfDayStart),
      end: minutesToTime(mergedBusy.length > 0 ? mergedBusy[0].start : golfDayEnd)
    });
  }

  // Find gaps between busy periods
  for (let i = 0; i < mergedBusy.length - 1; i++) {
    const gapStart = mergedBusy[i].end;
    const gapEnd = mergedBusy[i + 1].start;
    
    if (gapEnd > gapStart) {
      availableRanges.push({
        start: minutesToTime(gapStart),
        end: minutesToTime(gapEnd)
      });
    }
  }

  // Check if there's available time after the last busy period
  if (mergedBusy.length > 0 && mergedBusy[mergedBusy.length - 1].end < golfDayEnd) {
    availableRanges.push({
      start: minutesToTime(mergedBusy[mergedBusy.length - 1].end),
      end: minutesToTime(golfDayEnd)
    });
  }

  const filteredAvailableRanges = availableRanges.filter(range => range.start !== range.end);
  return filteredAvailableRanges;
};

/**
 * Convert available times to busy times by finding the gaps
 * Spans the full 24h day (00:00 - 24:00) so every timezone / season is covered.
 */
export const convertAvailableToBusyTimes = (availableTimeRanges) => {

  if (!availableTimeRanges || availableTimeRanges.length === 0) {
    // If no available times, entire day is busy
    return [{ start: '00:00', end: '24:00' }];
  }

  const golfDayStart = timeToMinutes('00:00'); // midnight
  const golfDayEnd = timeToMinutes('24:00');   // end of day

  // Convert available ranges to minutes and sort
  const availableMinutes = availableTimeRanges
    .map(range => ({
      start: Math.max(timeToMinutes(range.start), golfDayStart),
      end: Math.min(timeToMinutes(range.end), golfDayEnd)
    }))
    .sort((a, b) => a.start - b.start);

  // Merge overlapping available periods
  const mergedAvailable = [];
  for (const range of availableMinutes) {
    if (mergedAvailable.length === 0 || range.start > mergedAvailable[mergedAvailable.length - 1].end) {
      mergedAvailable.push(range);
    } else {
      mergedAvailable[mergedAvailable.length - 1].end = Math.max(mergedAvailable[mergedAvailable.length - 1].end, range.end);
    }
  }

  // Find busy periods (gaps between available periods)
  const busyRanges = [];

  // Check if there's busy time before the first available period
  if (mergedAvailable.length === 0 || mergedAvailable[0].start > golfDayStart) {
    busyRanges.push({
      start: minutesToTime(golfDayStart),
      end: minutesToTime(mergedAvailable.length > 0 ? mergedAvailable[0].start : golfDayEnd)
    });
  }

  // Find gaps between available periods
  for (let i = 0; i < mergedAvailable.length - 1; i++) {
    const gapStart = mergedAvailable[i].end;
    const gapEnd = mergedAvailable[i + 1].start;
    
    if (gapEnd > gapStart) {
      busyRanges.push({
        start: minutesToTime(gapStart),
        end: minutesToTime(gapEnd)
      });
    }
  }

  // Check if there's busy time after the last available period
  if (mergedAvailable.length > 0 && mergedAvailable[mergedAvailable.length - 1].end < golfDayEnd) {
    busyRanges.push({
      start: minutesToTime(mergedAvailable[mergedAvailable.length - 1].end),
      end: minutesToTime(golfDayEnd)
    });
  }

  const filteredBusyRanges = busyRanges.filter(range => range.start !== range.end);
  return filteredBusyRanges;
};

// Get availability for a specific date range
export const getAvailabilityInDateRange = async (startDate, endDate, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_availability')
      .select('*')
      .eq('user_id', user.id)
      .gte('availability_date', startDate)
      .lte('availability_date', endDate)
      .order('availability_date', { ascending: true });

    if (error) {
      console.error('Error fetching availability in date range:', error);
      throw error;
    }

    // Transform data to time range format
    const availabilityData = {};
    data?.forEach(record => {
      if (!availabilityData[record.availability_date]) {
        availabilityData[record.availability_date] = [];
      }
      if (record.start_time && record.end_time) {
        availabilityData[record.availability_date].push({
          start: record.start_time.substring(0, 5),
          end: record.end_time.substring(0, 5)
        });
      }
    });

    // Merge overlapping ranges for each date
    Object.keys(availabilityData).forEach(date => {
      availabilityData[date] = mergeOverlappingRanges(availabilityData[date]);
    });

    return { data: availabilityData, error: null };
  } catch (error) {
    console.error('Error in getAvailabilityInDateRange:', error);
    return { data: {}, error };
  }
};

/**
 * Pick a sensible default Smart Schedule preset based on the user's own
 * availability:
 *   - This Week if any rows fall in this week (Mon–Sun, ISO week).
 *   - Next Week if any fall in next week.
 *   - Custom: spans all availability rows from the earliest future date to the
 *     latest, capped at 30 days from earliest (so a single user with one slot
 *     three months out doesn't generate a 90-day analysis window).
 *   - Falls back to This Week if the user has no availability at all in the
 *     next 90 days.
 *
 * Returns { preset: 'this_week' | 'next_week' | 'custom', customRange? }.
 */
export const pickDefaultSmartSchedulePreset = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) return { preset: 'this_week' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Search a wider window than the 30-day span cap so we can find later
    // availability and shape the custom range around it.
    const searchHorizon = new Date(today);
    searchHorizon.setDate(today.getDate() + 90);

    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const { data, error } = await supabase
      .from('user_availability')
      .select('availability_date')
      .eq('user_id', user.id)
      .gte('availability_date', fmt(today))
      .lte('availability_date', fmt(searchHorizon))
      .order('availability_date', { ascending: true });

    if (error || !data || data.length === 0) {
      return { preset: 'this_week' };
    }

    // Compute Monday-of-this-week (ISO).
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const thisMon = new Date(today);
    thisMon.setDate(today.getDate() + diffToMonday);
    const thisSun = new Date(thisMon);
    thisSun.setDate(thisMon.getDate() + 6);
    const nextMon = new Date(thisMon);
    nextMon.setDate(thisMon.getDate() + 7);
    const nextSun = new Date(nextMon);
    nextSun.setDate(nextMon.getDate() + 6);

    const dates = data.map(r => r.availability_date);
    const hasInRange = (start, end) => {
      const s = fmt(start);
      const e = fmt(end);
      return dates.some(d => d >= s && d <= e);
    };

    if (hasInRange(thisMon, thisSun)) return { preset: 'this_week' };
    if (hasInRange(nextMon, nextSun)) return { preset: 'next_week' };

    // Custom: span from the earliest future availability to the latest, with
    // the END capped at earliest+30d so a single distant slot doesn't blow
    // the analysis window out.
    // IMPORTANT: parse YYYY-MM-DD strings as LOCAL midnight, not UTC.
    // `new Date('2026-06-01')` parses as UTC, which becomes the previous day
    // in any timezone west of UTC (or a few hours into the previous day in BST).
    // Splitting and using `new Date(y, m-1, d)` lands cleanly on local midnight.
    const parseLocal = (ymd) => {
      const [y, m, d] = ymd.split('-').map(Number);
      return new Date(y, m - 1, d);
    };

    const earliest = parseLocal(dates[0]);
    earliest.setHours(0, 0, 0, 0);
    if (earliest < today) earliest.setTime(today.getTime());

    const cap = new Date(earliest);
    cap.setDate(earliest.getDate() + 30);

    let latest = parseLocal(dates[dates.length - 1]);
    latest.setHours(23, 59, 59, 999);
    if (latest > cap) {
      latest = new Date(cap);
      latest.setHours(23, 59, 59, 999);
    }

    return { preset: 'custom', customRange: { startDate: earliest, endDate: latest } };
  } catch (err) {
    console.error('Error in pickDefaultSmartSchedulePreset:', err);
    return { preset: 'this_week' };
  }
};

// ENHANCED: Get user information from group membership with timeout protection
const getUserInfoFromGroupMembership = async (userId, groupId = null) => {
  try {
    // Set up timeout for user info fetch (5 seconds)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('User info fetch timeout')), 5000)
    );

    const queryPromise = (async () => {
      // Try to get user info from group membership table
      let query = supabase
        .from('group_members')
        .select('member_name, role, user_id')
        .eq('user_id', userId);

      // If we have a specific group ID, use it for more accurate results
      if (groupId) {
        query = query.eq('group_id', groupId);
      }

      // Use .maybeSingle() instead of .single() to avoid throwing on no rows
      return await query.limit(1).maybeSingle();
    })();

    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

    if (error) {
      console.error('Error fetching user info from group membership:', error);
    }

    if (import.meta.env.DEV) {
      console.log('getUserInfoFromGroupMembership result:', { userId, groupId, data, error });
    }

    if (data && data.member_name) {
      return {
        id: userId,
        email: `${data.member_name.toLowerCase().replace(' ', '.')}@member.local`, // Mock email
        member_name: data.member_name, // Direct access for SmartResultCard
        user_metadata: {
          first_name: data.member_name.split(' ')[0] || data.member_name,
          last_name: data.member_name.split(' ')[1] || '',
          full_name: data.member_name
        }
      };
    }

    // Fallback to a generic user object
    return {
      id: userId,
      email: `user-${userId.slice(-8)}@example.com`,
      user_metadata: {
        first_name: `User`,
        last_name: `${userId.slice(-4)}`,
        full_name: `User ${userId.slice(-4)}`
      }
    };
  } catch (error) {
    console.error('Error getting user info for:', userId, error);
    return {
      id: userId,
      email: `user-${userId.slice(-8)}@example.com`,
      user_metadata: {
        first_name: 'User',
        last_name: userId.slice(-4),
        full_name: `User ${userId.slice(-4)}`
      }
    };
  }
};

// ENHANCED: Get availability for multiple users with timeout protection and better error handling
export const getGroupMembersAvailability = async (userIds, startDate, endDate, groupId = null) => {
  try {
    // Validate inputs
    if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('Valid user IDs array is required');
    }

    if (!startDate || !endDate) {
      throw new Error('Start date and end date are required');
    }

    // Filter out any invalid user IDs
    const realUserIds = userIds.filter(id => id && typeof id === 'string');
    if (realUserIds.length === 0) {
      throw new Error('No valid user IDs provided');
    }

    // ENHANCED: Set up timeout protection for the entire operation (20 seconds)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Group availability fetch timed out')), 20000)
    );

    const dataFetchPromise = (async () => {
      // Calculate expanded date range (6 months back, 6 months forward from today)
      const today = new Date();
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      const sixMonthsFromNow = new Date(today);
      sixMonthsFromNow.setMonth(today.getMonth() + 6);

      const expandedStartDate = sixMonthsAgo.toISOString().split('T')[0];
      const expandedEndDate = sixMonthsFromNow.toISOString().split('T')[0];

      // Query with expanded date range to catch all availability data
      // Only query real user IDs to avoid UUID format errors with demo IDs
      let data = [];
      let error = null;
      
      if (realUserIds.length > 0) {
        const result = await supabase
          .from('user_availability')
          .select('*')
          .in('user_id', realUserIds)
          .gte('availability_date', expandedStartDate)
          .lte('availability_date', expandedEndDate)
          .order('availability_date', { ascending: true });
        
        data = result.data || [];
        error = result.error;
      }

      if (error) {
        throw error;
      }

      // Group availability data by user
      const availabilityByUser = {};
      if (data && data.length > 0) {
        data.forEach(record => {
          if (!availabilityByUser[record.user_id]) {
            availabilityByUser[record.user_id] = [];
          }
          availabilityByUser[record.user_id].push(record);
        });
      }

      // Get user info and process availability
      const groupedAvailability = {};

      // Process real users
      for (const userId of realUserIds) {
        // Get user info from group membership (more reliable than auth.users)
        const userInfo = await getUserInfoFromGroupMembership(userId, groupId);

        // Check if any of this user's records are calendar synced
        const userRecords = availabilityByUser[userId] || [];
        const hasCalendarSynced = userRecords.some(record => record.is_calendar_synced === true);

        // Initialize user availability structure
        groupedAvailability[userId] = {
          user: userInfo,
          availability: {},
          hasCalendarSynced // Include flag for calendar sync status
        };

        // Process availability records for this user
        userRecords.forEach(record => {
          if (!groupedAvailability[userId].availability[record.availability_date]) {
            groupedAvailability[userId].availability[record.availability_date] = [];
          }

          // Add time range to the array for this user and date
          if (record.start_time && record.end_time) {
            groupedAvailability[userId].availability[record.availability_date].push({
              start: record.start_time.substring(0, 5),
              end: record.end_time.substring(0, 5)
            });
          }
        });

        // Merge overlapping ranges for each user and date
        Object.keys(groupedAvailability[userId].availability).forEach(date => {
          groupedAvailability[userId].availability[date] = mergeOverlappingRanges(
            groupedAvailability[userId].availability[date]
          );
        });
      }

      return { data: groupedAvailability, error: null };
    })();

    // Race the data fetch against the timeout
    return await Promise.race([dataFetchPromise, timeoutPromise]);
  } catch (error) {
    console.error('Error in getGroupMembersAvailability:', error);
    if (error.message.includes('timeout') || error.message.includes('timed out')) {
      return { data: {}, error: new Error('Availability data fetch timed out. Please try again.') };
    }
    return { data: {}, error };
  }
};

// Clear all availability for a user
export const clearUserAvailability = async (currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('user_availability')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      console.error('Error clearing availability:', error);
      throw error;
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Error in clearUserAvailability:', error);
    return { data: null, error };
  }
};

// Update availability for specific dates (partial update)
export const updateAvailabilityForDates = async (dateTimeRanges, currentUser = null) => {
  try {
    const user = await getCurrentUser(currentUser);
    if (!user) throw new Error('User not authenticated');

    const dates = Object.keys(dateTimeRanges);

    // Delete existing availability for these specific dates
    const { error: deleteError } = await supabase
      .from('user_availability')
      .delete()
      .eq('user_id', user.id)
      .in('availability_date', dates);

    if (deleteError) {
      console.error('Error deleting existing availability:', deleteError);
      throw deleteError;
    }

    // Prepare new records for these dates
    const availabilityRecords = [];
    Object.entries(dateTimeRanges).forEach(([date, timeRanges]) => {
      if (timeRanges && timeRanges.length > 0) {
        timeRanges.forEach(range => {
          if (range.start && range.end) {
            availabilityRecords.push({
              user_id: user.id,
              availability_date: date,
              start_time: range.start,
              end_time: range.end,
              time_slots: range.start // Keep for backward compatibility
            });
          }
        });
      }
    });

    // Insert new records if any exist
    if (availabilityRecords.length > 0) {
      const { data, error: insertError } = await supabase
        .from('user_availability')
        .insert(availabilityRecords)
        .select();

      if (insertError) {
        console.error('Error inserting updated availability:', insertError);
        throw insertError;
      }

      return { data, error: null };
    }

    return { data: [], error: null };
  } catch (error) {
    console.error('Error in updateAvailabilityForDates:', error);
    return { data: null, error };
  }
};

// Check database schema and verify migration
export const checkDatabaseSchema = async (currentUser = null) => {
  try {

    const user = await getCurrentUser(currentUser);
    if (!user) {
      console.error('❌ User not authenticated for schema check');
      return { error: 'User not authenticated' };
    }
    
    // Try to query the table structure by attempting to insert a test record (will fail but show schema)
    const { error } = await supabase
      .from('user_availability')
      .select('preference_type')
      .eq('user_id', user.id)
      .limit(1);
    
    if (error) {
      console.error('❌ Database schema check failed:', error);
      if (error.message.includes('column "preference_type" does not exist')) {
        return { 
          error: 'Database migration not applied - preference_type column missing',
          requiresMigration: true
        };
      }
      return { error: error.message };
    }
    
    return { error: null };
  } catch (error) {
    console.error('❌ Exception in checkDatabaseSchema:', error);
    return { error: error.message };
  }
};

// ENHANCED: Get availability summary with timeout protection
export const getAvailabilitySummary = async (userId = null) => {
  try {
    // Set up timeout for availability summary fetch (10 seconds)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Availability summary fetch timeout')), 10000)
    );

    const summaryPromise = (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user && !userId) throw new Error('User not authenticated');

      const targetUserId = userId || user.id;

      const { data, error } = await supabase
        .from('user_availability')
        .select('availability_date, start_time, end_time, time_slots')
        .eq('user_id', targetUserId)
        .order('availability_date', { ascending: true });

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return { data: [], error: null };
      }

      // Group by date and calculate total hours
      const summary = {};
      data?.forEach((record) => {
        if (!summary[record.availability_date]) {
          summary[record.availability_date] = {
            date: record.availability_date,
            timeRanges: [],
            totalHours: 0
          };
        }

        if (record.start_time && record.end_time) {
          const startTime = record.start_time.substring(0, 5);
          const endTime = record.end_time.substring(0, 5);
          const range = { start: startTime, end: endTime };
          summary[record.availability_date].timeRanges.push(range);

          // Calculate hours for this range
          const [startHours, startMinutes] = startTime.split(':').map(Number);
          const [endHours, endMinutes] = endTime.split(':').map(Number);
          const startTotalMinutes = startHours * 60 + startMinutes;
          const endTotalMinutes = endHours * 60 + endMinutes;
          const durationHours = (endTotalMinutes - startTotalMinutes) / 60;
          summary[record.availability_date].totalHours += durationHours;
        }
      });

      return { data: Object.values(summary), error: null };
    })();

    // Race the summary fetch against the timeout
    return await Promise.race([summaryPromise, timeoutPromise]);
  } catch (error) {
    console.error('Error in getAvailabilitySummary:', error);
    if (error.message.includes('timeout')) {
      return { data: [], error: new Error('Availability summary fetch timed out. Please try again.') };
    }
    return { data: [], error };
  }
};

// Find common availability for multiple users (for group scheduling)
export const findCommonAvailability = async (userIds, startDate, endDate) => {
  try {
    const { data, error } = await supabase
      .from('user_availability')
      .select('user_id, availability_date, start_time, end_time')
      .in('user_id', userIds)
      .gte('availability_date', startDate)
      .lte('availability_date', endDate)
      .order('availability_date', { ascending: true });

    if (error) {
      console.error('Error finding common availability:', error);
      throw error;
    }

    // Group availability by date
    const dateAvailability = {};
    data?.forEach(record => {
      if (!dateAvailability[record.availability_date]) {
        dateAvailability[record.availability_date] = {};
      }
      if (!dateAvailability[record.availability_date][record.user_id]) {
        dateAvailability[record.availability_date][record.user_id] = [];
      }
      if (record.start_time && record.end_time) {
        dateAvailability[record.availability_date][record.user_id].push({
          start: record.start_time.substring(0, 5),
          end: record.end_time.substring(0, 5)
        });
      }
    });

    // Find overlapping time ranges for each date
    const commonAvailability = {};
    Object.entries(dateAvailability).forEach(([date, userAvailability]) => {
      const availableUsers = Object.keys(userAvailability);
      // Only process dates where all users have some availability
      if (availableUsers.length === userIds.length) {
        const overlaps = findTimeRangeOverlaps(Object.values(userAvailability));
        if (overlaps.length > 0) {
          commonAvailability[date] = overlaps;
        }
      }
    });

    return { data: commonAvailability, error: null };
  } catch (error) {
    console.error('Error in findCommonAvailability:', error);
    return { data: {}, error };
  }
};

// Helper function to find overlapping time ranges across multiple users
const findTimeRangeOverlaps = (userRangesArray) => {
  if (userRangesArray.length === 0) return [];

  // Start with the first user's ranges
  let commonRanges = [...userRangesArray[0]];

  // For each subsequent user, find overlaps with existing common ranges
  for (let i = 1; i < userRangesArray.length; i++) {
    const userRanges = userRangesArray[i];
    const newCommonRanges = [];

    commonRanges.forEach(commonRange => {
      userRanges.forEach(userRange => {
        const overlapStart = commonRange.start > userRange.start ? commonRange.start : userRange.start;
        const overlapEnd = commonRange.end < userRange.end ? commonRange.end : userRange.end;

        if (overlapStart < overlapEnd) {
          newCommonRanges.push({
            start: overlapStart,
            end: overlapEnd
          });
        }
      });
    });

    commonRanges = mergeOverlappingRanges(newCommonRanges);
  }

  return commonRanges;
};

// Get user availability summary for solo scheduling (within a date range)
export const getUserAvailabilitySummary = async (userId, startDate, endDate) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!startDate || !endDate) throw new Error('Start date and end date are required');

    // Set up timeout for availability summary fetch (10 seconds)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('User availability summary fetch timeout')), 10000)
    );

    const summaryPromise = (async () => {
      // Helper function to normalize dates
      const normalizeDate = (date) => {
        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        if (typeof date === 'string') {
          return date.split('T')[0];
        }
        return date;
      };

      const normalizedStartDate = normalizeDate(startDate);
      const normalizedEndDate = normalizeDate(endDate);

      // Check if user has calendar connected (same pattern as getUserAvailability)
      const { data: calendarConnection } = await supabase
        .from('calendar_connections')
        .select('id, sync_enabled, provider')
        .eq('user_id', userId)
        .eq('sync_enabled', true)
        .in('provider', ['outlook', 'google'])
        .maybeSingle();

      // Also check for ICS subscriptions
      const { data: icsSubscriptions } = await supabase
        .from('ics_subscriptions')
        .select('id, sync_enabled')
        .eq('user_id', userId)
        .eq('sync_enabled', true);

      const hasOAuthCalendar = calendarConnection && calendarConnection.sync_enabled;
      const hasIcsCalendar = icsSubscriptions && icsSubscriptions.length > 0;
      const isCalendarConnected = hasOAuthCalendar || hasIcsCalendar;

      if (isCalendarConnected) {
        // Calendar connected - get busy times and calculate available gaps
        const { data: busyData, error: busyError } = await supabase
          .from('user_availability')
          .select('availability_date, start_time, end_time')
          .eq('user_id', userId)
          .eq('is_calendar_synced', true)
          .gte('availability_date', normalizedStartDate)
          .lte('availability_date', normalizedEndDate)
          .order('availability_date', { ascending: true });

        if (busyError) {
          throw busyError;
        }

        // Group busy times by date
        const busyTimesByDate = {};
        busyData?.forEach((record) => {
          if (!busyTimesByDate[record.availability_date]) {
            busyTimesByDate[record.availability_date] = [];
          }
          if (record.start_time && record.end_time) {
            busyTimesByDate[record.availability_date].push({
              start: record.start_time.substring(0, 5),
              end: record.end_time.substring(0, 5)
            });
          }
        });

        // Generate all dates in range
        const allDatesInRange = [];
        const current = new Date(normalizedStartDate);
        const end = new Date(normalizedEndDate);
        while (current <= end) {
          allDatesInRange.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }

        // Calculate available time for each date
        let totalHours = 0;
        const datesWithAvailability = new Set();

        allDatesInRange.forEach(date => {
          const busyRanges = busyTimesByDate[date] || [];
          // Use convertBusyTimesToAvailable to get available gaps
          const availableRanges = convertBusyTimesToAvailable(busyRanges);

          // Calculate hours available on this date
          let dateHours = 0;
          availableRanges.forEach(range => {
            const [startHours, startMinutes] = range.start.split(':').map(Number);
            const [endHours, endMinutes] = range.end.split(':').map(Number);
            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = endHours * 60 + endMinutes;
            dateHours += (endTotalMinutes - startTotalMinutes) / 60;
          });

          if (dateHours > 0) {
            datesWithAvailability.add(date);
            totalHours += dateHours;
          }
        });

        const daysWithAvailability = datesWithAvailability.size;

        return {
          hasAvailability: daysWithAvailability > 0,
          availabilityInRange: daysWithAvailability,
          totalHoursInRange: totalHours,
          summary: daysWithAvailability > 0
            ? `${daysWithAvailability} day${daysWithAvailability !== 1 ? 's' : ''} with availability`
            : 'No availability set in this date range'
        };
      } else {
        // No calendar connected - use manual data (existing logic)
        const { data, error } = await supabase
          .from('user_availability')
          .select('availability_date, start_time, end_time, preference_type')
          .eq('user_id', userId)
          .eq('preference_type', 'available')
          .gte('availability_date', normalizedStartDate)
          .lte('availability_date', normalizedEndDate)
          .order('availability_date', { ascending: true });

        if (error) {
          throw error;
        }

        if (!data || data.length === 0) {
          return {
            hasAvailability: false,
            availabilityInRange: 0,
            totalHoursInRange: 0,
            summary: 'No availability set in this date range'
          };
        }

        // Calculate unique dates with availability
        const uniqueDates = new Set();
        let totalHours = 0;

        data.forEach((record) => {
          uniqueDates.add(record.availability_date);

          if (record.start_time && record.end_time) {
            const startTime = record.start_time.substring(0, 5);
            const endTime = record.end_time.substring(0, 5);

            const [startHours, startMinutes] = startTime.split(':').map(Number);
            const [endHours, endMinutes] = endTime.split(':').map(Number);
            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = endHours * 60 + endMinutes;
            const durationHours = (endTotalMinutes - startTotalMinutes) / 60;
            totalHours += durationHours;
          }
        });

        const daysWithAvailability = uniqueDates.size;
        const hasAvailability = daysWithAvailability > 0;

        return {
          hasAvailability,
          availabilityInRange: daysWithAvailability,
          totalHoursInRange: totalHours,
          summary: hasAvailability
            ? `${daysWithAvailability} day${daysWithAvailability !== 1 ? 's' : ''} with availability`
            : 'No availability set in this date range'
        };
      }
    })();

    return await Promise.race([summaryPromise, timeoutPromise]);
  } catch (error) {
    console.error('Error in getUserAvailabilitySummary:', error);
    if (error.message.includes('timeout')) {
      return {
        hasAvailability: false,
        availabilityInRange: 0,
        totalHoursInRange: 0,
        summary: 'Availability check timed out',
        error: error.message
      };
    }
    return {
      hasAvailability: false,
      availabilityInRange: 0,
      totalHoursInRange: 0,
      summary: 'Failed to check availability',
      error: error.message
    };
  }
};

// ENHANCED: Get group availability summary for smart scheduling
export const getGroupAvailabilitySummary = async (groupId, selectedMembers, startDate = null, endDate = null) => {
  try {
    
    // Validate inputs
    if (!selectedMembers || !Array.isArray(selectedMembers) || selectedMembers.length === 0) {
      console.warn('⚠️ No members selected for availability check');
      return {
        status: 'none',
        summary: 'No members selected',
        details: {},
        membersWithAvailability: []
      };
    }

    // Use provided date range or default to next 30 days
    let analysisStartDate, analysisEndDate;
    if (startDate && endDate) {
      analysisStartDate = startDate;
      analysisEndDate = endDate;
    } else {
      // Fallback to the next 30 days
      const today = new Date();
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      analysisStartDate = today.toISOString().split('T')[0];
      analysisEndDate = thirtyDaysFromNow.toISOString().split('T')[0];
    }

    // Helper function to normalize dates to YYYY-MM-DD format for consistent comparison
    const normalizeDate = (date) => {
      if (date instanceof Date) {
        // Use local timezone methods to avoid UTC conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      if (typeof date === 'string') {
        return date.split('T')[0]; // Handle both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss' formats
      }
      return date;
    };

    // Normalize the analysis dates for consistent comparison
    const normalizedStartDate = normalizeDate(analysisStartDate);
    const normalizedEndDate = normalizeDate(analysisEndDate);

    // Calculate 6-month date range from today for broader availability check
    const today = new Date();
    const sixMonthsFromNow = new Date(today);
    sixMonthsFromNow.setMonth(today.getMonth() + 6);
    
    const next6MonthsStartDate = today.toISOString().split('T')[0];
    const next6MonthsEndDate = sixMonthsFromNow.toISOString().split('T')[0];

    // Get group members availability using existing function
    const { data: groupAvailability, error } = await getGroupMembersAvailability(
      selectedMembers, 
      analysisStartDate, 
      analysisEndDate, 
      groupId
    );

    if (error) {
      console.error('❌ Error fetching group availability:', error);
      throw error;
    }


    // Helper function to check if a user has calendar-synced availability
    // Uses the hasCalendarSynced flag from groupAvailability data (set by getGroupMembersAvailability)
    // This avoids RLS issues with querying calendar_connections table
    const isUserCalendarConnected = (userId) => {
      const memberData = groupAvailability[userId];
      return memberData?.hasCalendarSynced || false;
    };

    // Generate all dates in the requested range
    const generateAllDatesInRange = (start, end) => {
      const dates = [];
      const current = new Date(start);
      const endDate = new Date(end);
      while (current <= endDate) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
      return dates;
    };
    const allRequestedDates = generateAllDatesInRange(normalizedStartDate, normalizedEndDate);

    // Process the availability data to create summary
    const details = {};
    const membersWithAvailability = [];
    let totalAvailabilityDays = 0;
    let totalMembers = selectedMembers.length;

    // Use for...of to support async/await
    for (const memberId of selectedMembers) {
      const memberData = groupAvailability[memberId];

      // Check if this member has calendar-synced availability data
      const hasCalendarConnected = isUserCalendarConnected(memberId);

      // Process availability based on calendar connection
      let processedAvailability = {};

      if (hasCalendarConnected) {
        // Calendar-connected user: convert busy times to available
        if (memberData && memberData.availability) {
          for (const [date, timeRanges] of Object.entries(memberData.availability)) {
            if (timeRanges && timeRanges.length > 0) {
              processedAvailability[date] = convertBusyTimesToAvailable(timeRanges);
            }
          }
        }
        // Fill in dates with NO records as fully available (full day)
        allRequestedDates.forEach(date => {
          if (!processedAvailability[date]) {
            processedAvailability[date] = [{ start: '00:00', end: '24:00' }];
          }
        });
      } else {
        // Non-calendar user: use raw availability data
        processedAvailability = memberData?.availability || {};
      }

      if (Object.keys(processedAvailability).length > 0) {
        const allAvailabilityDates = Object.keys(processedAvailability);

        // Filter availability dates for the selected date range
        const availabilityInRange = allAvailabilityDates.filter(date => {
          const normalizedDate = normalizeDate(date);
          const inRange = normalizedDate >= normalizedStartDate && normalizedDate <= normalizedEndDate;
          return inRange;
        });

        // Calculate total hours in selected range AND track longest consecutive block per day
        let totalHoursInRange = 0;
        const hoursPerDay = {}; // Track total hours for each day
        const longestBlockPerDay = {}; // Track longest consecutive block for each day

        availabilityInRange.forEach(date => {
          const timeRanges = processedAvailability[date] || [];
          let dayHours = 0;
          let longestBlock = 0;

          timeRanges.forEach(range => {
            // Parse time strings (HH:MM format)
            const [startHour, startMin] = range.start.split(':').map(Number);
            const [endHour, endMin] = range.end.split(':').map(Number);

            // Calculate duration in hours
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;
            const durationHours = (endMinutes - startMinutes) / 60;

            dayHours += durationHours;
            // Track the longest consecutive block
            if (durationHours > longestBlock) {
              longestBlock = durationHours;
            }
          });

          hoursPerDay[date] = dayHours;
          longestBlockPerDay[date] = longestBlock;
          totalHoursInRange += dayHours;
        });

        // Calculate qualifying days based on longest CONSECUTIVE block (not total hours)
        // 18 holes needs ≥4 consecutive hours, 9 holes needs ≥2 consecutive hours
        const daysWithMin4Hours = Object.values(longestBlockPerDay).filter(h => h >= 4).length;
        const daysWithMin2Hours = Object.values(longestBlockPerDay).filter(h => h >= 2).length;

        // Filter availability dates for the next 6 months
        const availabilityInNext6Months = allAvailabilityDates.filter(date => {
          const normalizedDate = normalizeDate(date);
          return normalizedDate >= next6MonthsStartDate && normalizedDate <= next6MonthsEndDate;
        });

        const hasAvailability = availabilityInRange.length > 0;
        const hasAnyAvailabilityInNext6Months = availabilityInNext6Months.length > 0;

        details[memberId] = {
          user: memberData.user,
          hasAvailability,
          availabilityDates: availabilityInRange.length, // Keep existing property for backward compatibility
          availabilityInRange: availabilityInRange.length,
          totalHoursInRange: totalHoursInRange, // Total hours of availability
          hoursPerDay, // Map of date -> hours for that day
          daysWithMin4Hours, // Days with ≥4 hours (for 18 holes)
          daysWithMin2Hours, // Days with ≥2 hours (for 9 holes)
          availabilityInNext6Months: availabilityInNext6Months.length,
          hasAnyAvailabilityInNext6Months,
          lastUpdate: availabilityInRange.length > 0 ? Math.max(...availabilityInRange.map(d => new Date(d).getTime())) : null
        };

        if (hasAvailability) {
          membersWithAvailability.push({
            userId: memberId,
            userInfo: memberData.user,
            availabilityDates: availabilityInRange.length
          });
          totalAvailabilityDays += availabilityInRange.length;
        }
      } else {
        // Member has no availability data
        details[memberId] = {
          user: memberData?.user || {
            id: memberId,
            user_metadata: { first_name: 'Unknown', full_name: 'Unknown Member' }
          },
          hasAvailability: false,
          availabilityDates: 0,
          availabilityInRange: 0,
          totalHoursInRange: 0,
          hoursPerDay: {},
          daysWithMin4Hours: 0,
          daysWithMin2Hours: 0,
          availabilityInNext6Months: 0,
          hasAnyAvailabilityInNext6Months: false,
          lastUpdate: null
        };
      }
    }

    // Determine overall status
    const availabilityPercentage = (membersWithAvailability.length / totalMembers) * 100;
    let status;
    let summary;

    if (availabilityPercentage >= 80) {
      status = 'good';
      summary = `${membersWithAvailability.length}/${totalMembers} members have availability set`;
    } else if (availabilityPercentage >= 50) {
      status = 'fair';
      summary = `${membersWithAvailability.length}/${totalMembers} members have availability set`;
    } else if (availabilityPercentage > 0) {
      status = 'poor';
      summary = `Only ${membersWithAvailability.length}/${totalMembers} members have availability set`;
    } else {
      status = 'none';
      summary = 'No members have availability set';
    }


    return {
      status,
      summary,
      details,
      membersWithAvailability
    };

  } catch (error) {
    console.error('❌ Error in getGroupAvailabilitySummary:', error);
    return {
      status: 'error',
      summary: 'Failed to load availability data',
      details: {},
      membersWithAvailability: [],
      error: error.message
    };
  }
};